<template>
    <Transition name="birthday-celebration-fade">
        <div v-if="visible" class="birthday-celebration" aria-live="polite">
            <div class="birthday-celebration__confetti" aria-hidden="true">
                <i v-for="particle in particles" :key="particle.id" :style="particle.style"></i>
            </div>
            <div class="birthday-celebration__balloons" aria-hidden="true">
                <i v-for="balloon in balloons" :key="balloon.id" :style="balloon.style"></i>
            </div>
            <div class="birthday-celebration__fireworks" aria-hidden="true">
                <span v-for="firework in fireworks" :key="firework.id" :style="firework.style">
                    <i v-for="spark in 12" :key="spark" :style="{ '--spark-index': spark - 1 }"></i>
                </span>
            </div>
            <div class="birthday-celebration__message">
                <span class="birthday-celebration__cake" aria-hidden="true">🎂</span>
                <div>
                    <small>HAPPY BIRTHDAY</small>
                    <strong>Chúc mừng sinh nhật {{ namesLabel }}!</strong>
                    <p>Chúc một ngày thật nhiều niềm vui và những điều tuyệt vời 🎉</p>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
    visible: { type: Boolean, default: false },
    names: { type: Array, default: () => [] },
});

const colors = ["#ff4f81", "#ffb020", "#3b82f6", "#22c55e", "#8b5cf6", "#06b6d4"];
const particles = Array.from({ length: 72 }, (_, index) => ({
    id: index,
    style: {
        "--x": `${(index * 37) % 100}vw`,
        "--delay": `${((index * 17) % 24) / 10}s`,
        "--duration": `${3.2 + ((index * 13) % 20) / 10}s`,
        "--rotate": `${180 + ((index * 43) % 540)}deg`,
        "--color": colors[index % colors.length],
    },
}));
const balloons = Array.from({ length: 12 }, (_, index) => ({
    id: index,
    style: {
        "--x": `${4 + ((index * 43) % 92)}vw`,
        "--delay": `${1.15 + ((index * 11) % 12) / 10}s`,
        "--duration": `${3.2 + ((index * 7) % 12) / 10}s`,
        "--sway": `${index % 2 ? 36 : -36}px`,
        "--color": colors[index % colors.length],
    },
}));
const fireworks = [
    { id: 1, style: { left: "12%", top: "20%", "--delay": "3s", "--color": "#ff4f81" } },
    { id: 2, style: { left: "88%", top: "20%", "--delay": "3.15s", "--color": "#ffb020" } },
];

const namesLabel = computed(() => {
    if (props.names.length <= 2) return props.names.join(" và ");
    return `${props.names.slice(0, 2).join(", ")} và ${props.names.length - 2} thành viên khác`;
});
</script>

<style scoped>
.birthday-celebration {
    position: fixed;
    inset: 0;
    z-index: 100000;
    overflow: hidden;
    pointer-events: none;
    background: radial-gradient(circle at center, rgba(255, 250, 235, .2), transparent 55%);
}
.birthday-celebration__confetti i {
    position: absolute;
    left: var(--x);
    top: -18px;
    width: 8px;
    height: 14px;
    border-radius: 2px;
    background: var(--color);
    animation: birthday-confetti-fall var(--duration) linear var(--delay) both;
}
.birthday-celebration__balloons i {
    position: absolute;
    left: var(--x);
    bottom: -100px;
    width: 38px;
    height: 48px;
    border-radius: 50% 50% 46% 46%;
    background: var(--color);
    box-shadow: inset -7px -5px 0 rgba(0, 0, 0, .08), inset 7px 5px 0 rgba(255, 255, 255, .18);
    opacity: 0;
    animation: birthday-balloon-rise var(--duration) ease-in var(--delay) both;
}
.birthday-celebration__balloons i::before {
    content: "";
    position: absolute;
    left: 16px;
    bottom: -7px;
    border-width: 0 4px 8px;
    border-style: solid;
    border-color: transparent transparent var(--color);
}
.birthday-celebration__balloons i::after {
    content: "";
    position: absolute;
    left: 19px;
    top: 53px;
    width: 1px;
    height: 68px;
    background: rgba(88, 88, 88, .45);
}
.birthday-celebration__fireworks > span {
    position: absolute;
    left: var(--left);
    top: var(--top);
    width: 1px;
    height: 1px;
    animation: birthday-firework-pop 2.4s ease-out var(--delay) both;
}
.birthday-celebration__fireworks > span > i {
    --angle: calc(var(--spark-index) * 30deg);
    position: absolute;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--color);
    box-shadow: 0 0 8px var(--color);
    animation: birthday-spark 1.1s ease-out var(--delay) both;
    transform: rotate(var(--angle)) translateX(0);
}
.birthday-celebration__message {
    position: absolute;
    left: 50%;
    top: 50%;
    width: min(520px, calc(100vw - 40px));
    display: flex;
    align-items: center;
    gap: 18px;
    padding: 22px 26px;
    border: 1px solid rgba(255, 190, 94, .65);
    border-radius: 18px;
    background: rgba(255, 255, 255, .94);
    box-shadow: 0 20px 60px rgba(102, 61, 13, .22);
    transform: translate(-50%, -50%);
    animation: birthday-message-pop .55s cubic-bezier(.2, .9, .3, 1.25) .65s both;
}
.birthday-celebration__cake {
    font-size: 54px;
    animation: birthday-cake-appear .55s cubic-bezier(.2, .9, .3, 1.35) 2.2s both,
        birthday-cake-dance 1.2s ease-in-out 2.75s infinite;
}
.birthday-celebration__message small { display: block; margin-bottom: 4px; color: #e88613; font-size: 11px; font-weight: 800; letter-spacing: 1.6px; }
.birthday-celebration__message strong { display: block; color: #2d3440; font-size: 20px; line-height: 1.35; }
.birthday-celebration__message p { margin: 5px 0 0; color: #737b89; font-size: 13px; line-height: 1.5; }
.birthday-celebration-fade-leave-active { transition: opacity .5s ease; }
.birthday-celebration-fade-leave-to { opacity: 0; }
@keyframes birthday-confetti-fall {
    0% { opacity: 0; transform: translate3d(0, -20px, 0) rotate(0); }
    8% { opacity: 1; }
    100% { opacity: .9; transform: translate3d(35px, calc(100vh + 40px), 0) rotate(var(--rotate)); }
}
@keyframes birthday-balloon-rise {
    0% { opacity: 0; transform: translate3d(0, 0, 0) rotate(-3deg); }
    8% { opacity: .95; }
    100% { opacity: .9; transform: translate3d(var(--sway), calc(-100vh - 150px), 0) rotate(5deg); }
}
@keyframes birthday-spark {
    0% { opacity: 0; transform: rotate(var(--angle)) translateX(0) scale(.2); }
    12% { opacity: 1; }
    100% { opacity: 0; transform: rotate(var(--angle)) translateX(76px) scale(0); }
}
@keyframes birthday-firework-pop { 0%, 100% { opacity: 1; } }
@keyframes birthday-message-pop {
    from { opacity: 0; transform: translate(-50%, -44%) scale(.82); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}
@keyframes birthday-cake-appear {
    from { opacity: 0; transform: translateY(18px) scale(.25) rotate(-12deg); }
    to { opacity: 1; transform: translateY(0) scale(1) rotate(-3deg); }
}
@keyframes birthday-cake-dance {
    0%, 100% { transform: translateY(0) rotate(-3deg); }
    50% { transform: translateY(-7px) rotate(4deg); }
}
@media (prefers-reduced-motion: reduce) {
    .birthday-celebration__confetti,
    .birthday-celebration__fireworks,
    .birthday-celebration__balloons { display: none; }
    .birthday-celebration__cake,
    .birthday-celebration__message { animation: none; }
}
@media (max-width: 520px) {
    .birthday-celebration__message { padding: 18px; gap: 12px; }
    .birthday-celebration__cake { font-size: 42px; }
    .birthday-celebration__message strong { font-size: 16px; }
}
</style>
